import React, {
  useMemo,
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import Table from '@components/Table';
import { useTranslation } from 'react-i18next';
import {
  FileSearchOutlined,
  CopyOutlined,
  ControlOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  DatePicker,
  ConfigProvider,
  Button,
  Select,
  Tooltip,
  Divider,
  Space,
} from 'antd';
import { formatCurrency, getFirstAndLastDayOfMonth } from '@utils/formatters';
import moment, { Moment } from 'moment';
import useSwr from '@hooks/useSwr';
import ptBR from 'antd/es/locale/pt_BR';
import { useNavigate } from 'react-router-dom';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import SessionContext from '@providers/Session/SessionContext';
import useSearchParams from '@hooks/useSearchParams';
import SelectCustomers from '@components/SelectCustomers';
import RoleChecker from '@components/RoleChecker';
import CustomPopover from '@components/CustomPopover';
import rolesConstants from '@utils/rolesConstants';
import CustomTag from '@components/CustomTag';
import AddItemsToInvoice from './AddItemsToInvoice';
import RequestInvoiceReport from './RequestInvoiceReport';

const InvoicesMot = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['invoices', 'attributes']);

  const { keycloak } = useContext(KeycloakContext);
  const {
    hasCustomersRoles,
    customer: { customersSelectOptions },
  } = useContext(SessionContext);
  const customerType = keycloak?.idTokenParsed?.customerType;
  const isEndCustomer = customerType === 'CUSTOMER';
  const customerIdLogged = keycloak?.idTokenParsed?.customerId;

  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const [showRequestReport, setShowRequestReport] = useState(false);

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['customerId'],
    extraParams: [
      'status',
      'protocol',
      'createdBy',
      'startDate',
      'endDate',
      'number',
    ],
  });

  const { customerId, status, startDate, endDate, page, linesPerPage } =
    useMemo(() => searchParams, [searchParams]);

  const isStatusClosed = status === 'C';
  const handleAttrEmpty = (attr: any) => (attr === '' ? undefined : attr);

  const { data } = useSwr(
    customerId ? `/service-proxy/broker/${customerId}/invoices` : null,
    {
      params: {
        customerId,
        status: handleAttrEmpty(status),
        initialDate: handleAttrEmpty(startDate),
        endDate: handleAttrEmpty(endDate),
      },
    },
  );

  useEffect(() => {
    if (isEndCustomer && !customerId) {
      setSearchParams({ customerId: customerIdLogged });
    }
  }, [customerId, customerIdLogged, isEndCustomer, setSearchParams]);

  const statusOptions = useMemo(
    () => [
      { label: t('searchResultItem.opened'), value: 'O' },
      { label: t('searchResultItem.closed'), value: 'C' },
    ],
    [t],
  );

  const handleDateChange = useCallback(
    (date: Moment | null) => {
      if (date) {
        const { firstDay, lastDay } = getFirstAndLastDayOfMonth(date.toDate());
        setSearchParams({ startDate: firstDay, endDate: lastDay });
      } else {
        setSearchParams({ startDate: undefined, endDate: undefined });
      }
    },
    [setSearchParams],
  );

  const columnTotalPrice = useCallback(
    (nuValorFatura: string, record: any) => {
      const {
        nuValorMvnoAlgar,
        nuValorConectividadeLorawan,
        nuValorServicoAgregadoRedeMovel,
      } = record;

      const dataContent = [
        {
          title: t('searchResultItem.nuValorServicoAgregadoRedeMovel'),
          value: formatCurrency(Number(nuValorServicoAgregadoRedeMovel)),
          show: !!nuValorServicoAgregadoRedeMovel,
        },
        {
          title: t('searchResultItem.nuValorConectividadeLorawan'),
          value: formatCurrency(Number(nuValorConectividadeLorawan)),
          show: !!nuValorConectividadeLorawan,
        },
        {
          title: t('searchResultItem.nuValorMvnoAlgar'),
          value: formatCurrency(Number(nuValorMvnoAlgar)),
          show: !!nuValorMvnoAlgar,
        },
      ];

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span>{formatCurrency(Number(nuValorFatura))}</span>
          {dataContent.filter(item => item.show).length > 0 && (
            <CustomPopover
              title={t('searchResultItem.totalValue')}
              titleValue={formatCurrency(Number(nuValorFatura))}
              dataContent={dataContent}
            >
              <Button type="link" icon={<InfoCircleOutlined />} />
            </CustomPopover>
          )}
        </div>
      );
    },
    [t],
  );

  const columns = useMemo(
    () => [
      {
        title: t('searchResultItem.id'),
        align: 'center' as const,
        dataIndex: 'idFatura',
      },
      {
        title: t('searchResultItem.status'),
        dataIndex: 'idStatusFatura',
        align: 'center' as const,
        render: (value: string) => (
          <CustomTag color={value === 'O' ? 'green' : 'red'}>
            {value === 'O'
              ? t('searchResultItem.opened')
              : t('searchResultItem.closed')}
          </CustomTag>
        ),
      },
      {
        title: t('searchResultItem.totalValue'),
        dataIndex: 'nuValorFatura',
        align: 'center' as const,
        render: columnTotalPrice,
      },
      {
        title: t('searchResultItem.totalTraffic'),
        dataIndex: 'nuTrafegoTotal',
        align: 'center' as const,
      },
      {
        title: t('searchResultItem.terminals'),
        align: 'center' as const,
        dataIndex: 'nuTerminais',
      },
      {
        title: t('searchResultItem.endDate'),
        dataIndex: 'dataTermino',
        align: 'center' as const,
        render: (value: string) => value.replaceAll('-', '/'),
      },
      {
        title: t('searchResultItem.expirationDate'),
        dataIndex: 'dataVencimento',
        align: 'center' as const,
        render: (value: string) => value.replaceAll('-', '/'),
      },
      {
        title: t('details'),
        dataIndex: 'details',
        align: 'center' as const,
        render: (_: any, record: { idFatura: any }) => (
          <Button
            icon={<FileSearchOutlined />}
            onClick={() => {
              navigate(`${record.idFatura}/${customerId}`);
            }}
          />
        ),
      },
    ],
    [t, columnTotalPrice, navigate, customerId],
  );

  const extraActions = useMemo(
    () => (
      <RoleChecker role={rolesConstants.REQUEST_REPORT}>
        <Tooltip title={t('report.request')}>
          <Button
            icon={<CopyOutlined />}
            onClick={() => setShowRequestReport(true)}
          />
        </Tooltip>
      </RoleChecker>
    ),
    [t],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: hasCustomersRoles,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('search.customerPlaceholder'),
        element: (
          <SelectCustomers
            options={customersSelectOptions}
            allowClear
            value={customerId && Number(customerId)}
            onChange={(cstId: number) => setSearchParams({ customerId: cstId })}
          />
        ),
      },
      {
        visible: !!customerId,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('search.statusPlaceholder'),
        element: (
          <Select
            options={statusOptions}
            style={{ width: '100%' }}
            allowClear
            value={status}
            onChange={value => {
              setSearchParams({
                status: value,
              });
              if (!value || value === 'O') {
                setSearchParams({
                  status: value,
                  initialDate: undefined,
                  endDate: undefined,
                });
              }
            }}
          />
        ),
      },
      {
        visible: isStatusClosed && !!customerId,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('search.datesRangePlaceholder'),
        element: (
          <ConfigProvider locale={ptBR}>
            <DatePicker
              format="MMMM/YYYY"
              style={{ width: '100%' }}
              disabledDate={d => {
                return d && d >= moment().startOf('month');
              }}
              // @ts-ignore
              defaultValue={endDate ? moment(endDate, 'YYYYMMDD') : undefined}
              onChange={handleDateChange}
              picker="month"
            />
          </ConfigProvider>
        ),
      },
    ],
    [
      hasCustomersRoles,
      t,
      customersSelectOptions,
      customerId,
      statusOptions,
      status,
      isStatusClosed,
      endDate,
      handleDateChange,
      setSearchParams,
    ],
  );

  return (
    <>
      <RoleChecker role={rolesConstants.ADD_ITEMS_TO_INVOICE}>
        <AddItemsToInvoice
          visible={showAddItemsModal}
          onClose={() => setShowAddItemsModal(false)}
        />
      </RoleChecker>
      <RoleChecker role={rolesConstants.REQUEST_REPORT}>
        <RequestInvoiceReport
          visible={showRequestReport}
          statusOptions={statusOptions}
          onClose={() => setShowRequestReport(false)}
        />
      </RoleChecker>
      <Table
        columns={columns}
        data={data?.listaFaturasVirtuais || []}
        loading={!data && data !== '' && !!customerId}
        title={t('title')}
        tableKey="invoicesMot"
        extraActions={extraActions}
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          openByDefault: true,
          onClearFilters: () => clearSearchParams(),
        }}
        pagination={{ page, linesPerPage, total: data?.totalElements }}
        onTableChange={setSearchParams}
        noPagination
        mainExtraAction={
          <RoleChecker role={rolesConstants.VIEW_INVOICE_ACTIONS}>
            <Space>
              <Divider orientation="center" type="vertical" />
              <Tooltip title={t('invoice-actions')}>
                <Button
                  icon={<ControlOutlined />}
                  onClick={() => navigate('actions')}
                />
              </Tooltip>
            </Space>
          </RoleChecker>
        }
      />
    </>
  );
};

export default InvoicesMot;
